<template>
  <div>

    <!-- Period selector -->
    <div class="d-flex align-items-center mb-3">
      <span class="text-uppercase text-secondary font-weight-semibold small mr-3">PERIOD</span>
      <div class="btn-group btn-group-sm" role="group">
        <button
          v-for="period in periods"
          :key="period"
          type="button"
            class="btn btn-outline-secondary"
          :class="{ active: selectedPeriod === period }"
          @click="selectedPeriod = period"
        >{{ period }}d</button>
      </div>
    </div>

    <!-- Loading spinner -->
    <div v-if="loading" class="text-center p-5">
      <b-spinner label="Loading..." />
    </div>

    <div v-else>

      <!-- Stat cards row -->
      <template v-if="hasReadings">
        <div v-if="latestBp" class="row no-gutters mb-3">
          <div class="col p-2 text-center border rounded-left">
            <div class="font-weight-semibold mb-0">
              <template v-if="bpData.optimalRange.systolicUpper">{{ bpData.optimalRange.systolicUpper }}/{{ bpData.optimalRange.diastolicUpper }}</template>
              <template v-else>—</template>
            </div>
            <div class="text-uppercase text-secondary font-weight-semibold smaller">Goal BP</div>
          </div>
          <div class="col p-2 text-center border border-left-0">
            <div class="font-weight-semibold mb-0">{{ latestBp.systolic }}/{{ latestBp.diastolic }}</div>
            <div class="text-uppercase text-secondary font-weight-semibold smaller">Latest BP</div>
          </div>
          <div class="col p-2 text-center border border-left-0">
            <div class="font-weight-semibold mb-0 d-flex align-items-start justify-content-center flex-nowrap">
              <span v-if="avgBp.sbpTrend !== null" :class="trendClass(avgBp.sbpTrend)" class="trend-badge">
                {{ trendSign(avgBp.sbpTrend) }}{{ Math.abs(avgBp.sbpTrend) }}
              </span>
              <span>{{ avgBp.systolic }}/{{ avgBp.diastolic }}</span>
              <span v-if="avgBp.dbpTrend !== null" :class="trendClass(avgBp.dbpTrend)" class="trend-badge">
                {{ trendSign(avgBp.dbpTrend) }}{{ Math.abs(avgBp.dbpTrend) }}
              </span>
            </div>
            <div class="text-uppercase text-secondary font-weight-semibold smaller">Avg BP</div>
          </div>
          <div class="col p-2 text-center border border-left-0">
            <div class="font-weight-semibold mb-0">{{ inRangePct.sbp !== null ? inRangePct.sbp + '%' : '—' }}</div>
            <div class="text-uppercase text-secondary font-weight-semibold smaller">In Range SBP</div>
          </div>
          <div class="col p-2 text-center border border-left-0 rounded-right">
            <div class="font-weight-semibold mb-0">{{ inRangePct.dbp !== null ? inRangePct.dbp + '%' : '—' }}</div>
            <div class="text-uppercase text-secondary font-weight-semibold smaller">In Range DBP</div>
          </div>
        </div>

        <div v-else class="alert alert-info">
          No blood pressure data available in this range. Select a different range.
        </div>

        <!-- Chart / Table toggle + views (only when there are readings) -->
        <div class="d-flex justify-content-end mb-2">
          <div
              v-if="showCopyButton"
              class="btn btn-outline-secondary btn-sm mr-2"
               v-b-tooltip.hover
               title="Copy BP data for selected period"
               @click="copyTableToClipboard">
            <span class="fa fa-copy"></span>
          </div>
          <div class="btn-group btn-group-sm" role="group">
            <button
                type="button"
                class="btn btn-outline-secondary"
                :class="{ active: activeView === 'chart' }"
                @click="activeView = 'chart'"
            >Chart</button>
            <button
                type="button"
                class="btn btn-outline-secondary"
                :class="{ active: activeView === 'table' }"
                @click="activeView = 'table'"
            >Table</button>
          </div>
        </div>

        <!-- Scatter Chart -->
        <figure v-if="activeView === 'chart'">
          <apexchart
              type="scatter"
              height="320"
              :series="chartSeries"
              :options="chartOptions"
          />

          <!-- Legend -->
          <figcaption class="d-flex small text-secondary">
            <div>
              <div>
                SBP:
                <ul class="d-inline-block list-inline mb-0">
                  <li class="list-inline-item">
                    <span class="legend-circle bp-value-high-bg" /> Above Range
                  </li>
                  <li class="list-inline-item">
                    <span class="legend-circle bp-value-normal-bg" /> In Range
                  </li>
                  <li class="list-inline-item">
                    <span class="legend-circle bp-value-low-bg" /> Below Range
                  </li>
                </ul>
              </div>
              <div>
                DBP:
                <ul class="d-inline-block list-inline mb-0">
                  <li class="list-inline-item">
                    <span class="legend-triangle bp-value-high" /> Above Range
                  </li>
                  <li class="list-inline-item">
                    <span class="legend-triangle bp-value-normal" /> In Range
                  </li>
                  <li class="list-inline-item">
                    <span class="legend-triangle bp-value-low" /> Below Range
                  </li>
                </ul>
              </div>
            </div>
            <div class="border-left ml-3 pl-3">
              <span class="legend-range-box small">Optimal Range</span>
            </div>
          </figcaption>
          <div>

          </div>
        </figure>

        <!-- Table -->
        <div v-show="activeView === 'table'">
          <table ref="bpTable" class="table table-sm table-hover small text-secondary">
            <thead>
            <tr>
              <th class="text-uppercase font-weight-semibold">Date & Time</th>
              <th class="text-uppercase font-weight-semibold">SBP</th>
              <th class="text-uppercase font-weight-semibold">DBP</th>
              <th class="text-uppercase font-weight-semibold">Classification</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(row, i) in tableRows" :key="i">
              <td class="">
                <div class="font-weight-semibold">{{ row.date }}</div>
                <div class="small">{{ row.time }}</div>
              </td>
              <td>
                <span :class="getClassForBP(row.sbpStatus)">{{ row.systolic }} <span class="bp-unit">mmHg</span></span>
              </td>
              <td>
                <span :class="getClassForBP(row.dbpStatus)">{{ row.diastolic }} <span class="bp-unit">mmHg</span></span>
              </td>
              <td>
                <span :class="classificationPill(row.category)">{{ row.category }}</span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

      </template>
      <div v-else-if="!hasReadings" class="alert alert-info">
        No blood pressure data for this participant.
      </div>
      <div v-else class="alert alert-warning">
        Could not load blood pressure data.
      </div>

    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { BSpinner } from 'bootstrap-vue';

export default {
  name: 'CardiologyDrawer',

  components: {
    apexchart: VueApexCharts,
    BSpinner,
  },

  props: {
    endpoint:      { type: String, default: null },
    mrn:           { type: String, default: null },
    studyId:       { type: Number, default: null },
    participantId: { type: Number, default: null },
  },

  data() {
    return {
      loading: true,
      periods: [7, 14, 30, 90],
      selectedPeriod: 14,
      activeView: 'chart',
      bpData: null,
    };
  },

  computed: {
    filteredReadings() {
      if (!this.bpData || !this.bpData.readings) {return [];}
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - this.selectedPeriod);
      return this.bpData.readings.filter(r => new Date(r.date.replace(' ', 'T')) >= cutoff);
    },

    latestBp() {
      if (!this.filteredReadings.length) { return null; }
      const last = this.filteredReadings[this.filteredReadings.length - 1];
      return { systolic: last.systolic, diastolic: last.diastolic };
    },

    hasReadings() {
      return !!this.bpData?.readings?.length;
    },

    avgBp() {
      if (!this.filteredReadings.length) { return null; }
      const avg = (arr, key) => arr.reduce((s, r) => s + r[key], 0) / arr.length;
      const avgSys  = Math.round(avg(this.filteredReadings, 'systolic'));
      const avgDias = Math.round(avg(this.filteredReadings, 'diastolic'));

      // Only show trend if there is data going back 2x the current period
      let sbpTrend = null;
      let dbpTrend = null;
      if (this.selectedPeriod !== 90 && this.bpData && this.bpData.readings.length) {
        const now = new Date();
        const periodStart = new Date(now);
        periodStart.setDate(periodStart.getDate() - this.selectedPeriod);
        const prevPeriodStart = new Date(now);
        prevPeriodStart.setDate(prevPeriodStart.getDate() - this.selectedPeriod * 2);
        const oldest = new Date(this.bpData.readings[0].date.replace(' ', 'T'));
        if (oldest <= prevPeriodStart) {
          const prevReadings = this.bpData.readings.filter(r => {
            const d = new Date(r.date.replace(' ', 'T'));
            return d >= prevPeriodStart && d < periodStart;
          });
          if (prevReadings.length) {
            sbpTrend = Math.round(avgSys  - avg(prevReadings, 'systolic'));
            dbpTrend = Math.round(avgDias - avg(prevReadings, 'diastolic'));
          }
        }
      }

      return { systolic: avgSys, diastolic: avgDias, sbpTrend, dbpTrend };
    },

    inRangePct() {
      if (!this.filteredReadings.length || !this.bpData) { return { sbp: null, dbp: null }; }
      const range = this.bpData.optimalRange;
      if (range.systolicUpper === null && range.diastolicUpper === null) { return { sbp: null, dbp: null }; }

      let inSbp = 0;
      let inDbp = 0;
      this.filteredReadings.forEach(r => {
        if (r.systolic <= range.systolicUpper && r.systolic >= range.systolicLower) { inSbp++; }
        if (r.diastolic <= range.diastolicUpper && r.diastolic >= range.diastolicLower) { inDbp++; }
      });

      const count = this.filteredReadings.length;
      return {
        sbp: Math.round(inSbp / count * 100),
        dbp: Math.round(inDbp / count * 100),
      };
    },

    chartSeries() {
      if (!this.filteredReadings.length || !this.bpData) { return []; }

      const series = {
        sbpAbove: { name: 'SBP Above Range', data: [] },
        sbpIn:    { name: 'SBP In Range',    data: [] },
        sbpBelow: { name: 'SBP Below Range', data: [] },
        dbpAbove: { name: 'DBP Above Range', data: [] },
        dbpIn:    { name: 'DBP In Range',    data: [] },
        dbpBelow: { name: 'DBP Below Range', data: [] },
      };

      const range = this.bpData.optimalRange;

      this.filteredReadings.forEach(reading => {
        const xTs = new Date(reading.date.replace(' ', 'T')).getTime();

        // SBP
        let sbpKey;
        if (reading.systolic > range.systolicUpper) { sbpKey = 'sbpAbove'; }
        else if (reading.systolic < range.systolicLower) { sbpKey = 'sbpBelow'; }
        else { sbpKey = 'sbpIn'; }

        // DBP
        let dbpKey;
        if (reading.diastolic > range.diastolicUpper) { dbpKey = 'dbpAbove'; }
        else if (reading.diastolic < range.diastolicLower) { dbpKey = 'dbpBelow'; }
        else { dbpKey = 'dbpIn'; }

        series[sbpKey].data.push({
          x: xTs,
          y: reading.systolic,
          meta: {
            datetime: reading.date,
            type: 'SBP',
            SBP: {
              value: reading.systolic,
              series: sbpKey,
            },
            DBP: {
              value: reading.diastolic,
              series: dbpKey,
            },
            sourceName: reading.sourceName,
            category: reading.category
          },
        });

        series[dbpKey].data.push({
          x: xTs,
          y: reading.diastolic,
          meta: {
            datetime: reading.date,
            type: 'DBP',
            SBP: {
              value: reading.systolic,
              series: sbpKey,
            },
            DBP: {
              value: reading.diastolic,
              series: dbpKey,
            },
            sourceName: reading.sourceName,
            category: reading.category
          },
        });
      });

      return Object.values(series);
    },

    chartOptions() {
      if (!this.bpData) { return {}; }
      const range = this.bpData.optimalRange;
      const self = this;
      const min = new Date();
      const max = new Date();
      min.setDate(max.getDate() - this.selectedPeriod);

      return {
        chart: {
          type: 'scatter',
          zoom: { enabled: false },
          toolbar: { show: false },
          animations: { enabled: false },
        },
        markers: {
          size: [7, 7, 7, 9, 9, 9],
          shape: ['circle', 'circle', 'circle', 'triangle', 'triangle', 'triangle'],
          colors: ['#dc3545', '#28a745', '#007bff', '#dc3545', '#28a745', '#007bff'],
          strokeWidth: 0,
        },
        xaxis: {
          type: 'datetime',
          min: min.getTime(),
          max: max.getTime(),
          labels: {
            datetimeUTC: false,
            format: 'M/d',
          },
          tooltip: { enabled: false },
        },
        yaxis: {
          min: 50,
          max: 185,
          title: {
            text: 'Blood Pressure (mmHg)',
            style: { fontSize: '11px', color: '#6c757d' },
          },
          tickAmount: 5,
        },
        annotations: {
          yaxis: [
            {
              y: range.systolicLower,
              y2: range.systolicUpper,
              fillColor: '#28a745',
              opacity: 0.12,
            },
            {
              y: range.diastolicLower,
              y2: range.diastolicUpper,
              fillColor: '#28a745',
              opacity: 0.12,
              label: { text: '' },
            },
          ],
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom: ({seriesIndex, dataPointIndex, w}) => {
            return this.buildTooltip({ seriesIndex, dataPointIndex, w });
          }
        },
        legend: { show: false },
        grid: {
          borderColor: '#e9ecef',
          xaxis: {
            lines: {
              show: true,
            },
          },
          yaxis: {
            lines: {
              show: true,
            },
          },
        },
      };
    },

    tableRows() {
      if (!this.filteredReadings.length) { return []; }
      const range = this.bpData.optimalRange;
      return this.filteredReadings.map(reading => {
        const dateObj = new Date(reading.date.replace(' ', 'T'));
        const formattedDate = dateObj.toLocaleDateString('en-US', {
          month: 'numeric',  // "4"
          day: 'numeric',    // "19"
          year: '2-digit'    // "26"
        });
        const formattedTime = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        const sbpStatus = reading.systolic > range.systolicUpper ? 'high'
          : reading.systolic < range.systolicLower ? 'low' : 'normal';
        const dbpStatus = reading.diastolic > range.diastolicUpper ? 'high'
          : reading.diastolic < range.diastolicLower ? 'low' : 'normal';
        const category = reading.category.split(" ")
            .map(word => word[0].toUpperCase() + word.slice(1))
            .join(" ");
        return {
          date:       formattedDate,
          time:       formattedTime,
          systolic:   reading.systolic,
          diastolic:  reading.diastolic,
          sbpStatus,
          dbpStatus,
          category,
        };
      }).reverse();
    },

    showCopyButton() {
      return !!navigator.clipboard;
    }
  },

  mounted() {
    this.fetchData();
  },

  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const url = this.endpoint || `/api/v2/participants/${this.participantId}/cardiologyData`;
        const res = await fetch(url, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        this.bpData = (await res.json()).data;
        const readings = this.bpData?.readings;
        if (readings && readings.length) {
          // dynamically change the starting selectedPeriod based on the date range of available data
          const now = new Date();
          const msToDays = ms => ms / (1000 * 60 * 60 * 24);
          const oldestDaysAgo = msToDays(now - new Date(readings[0].date.replace(' ', 'T')));
          const newestDaysAgo = msToDays(now - new Date(readings[readings.length - 1].date.replace(' ', 'T')));
          if (oldestDaysAgo <= 7)      { this.selectedPeriod = 7; }
          else if (newestDaysAgo > 30) { this.selectedPeriod = 90; }
          else if (newestDaysAgo > 14) { this.selectedPeriod = 30; }
          else                         { this.selectedPeriod = 14; }
        }

      } catch {
        this.bpData = null;
      } finally {
        this.loading = false;
      }
    },

    buildTooltip({ seriesIndex, dataPointIndex, w }) {
      const series = w.config.series[seriesIndex];
      const data = series.data[dataPointIndex];
      if (!data) { return ''; }
      const date = new Date(data.x);
      const dateStr = date.toLocaleDateString("en-US", {
        weekday: 'short',    // "Sat"
        month: 'long',       // "March"
        day: 'numeric',      // "10"
        year: 'numeric',     // "2026"
      });
      const timeStr = date.toLocaleTimeString('en-US', {

        hour: 'numeric',     // "9"
        minute: '2-digit',   // "55"
        hour12: true         // "AM/PM"
      });
      const sbpClass = [
          'sbp-value',
          data.meta.SBP.series,
      ];
      const dbpClass = [
          'dbp-value',
          data.meta.DBP.series,
      ];
      if (seriesIndex < 3) {
        sbpClass.push('font-weight-semibold');
      } else {
        dbpClass.push('font-weight-semibold');
      }
      const category = data.meta.category.split(" ")
          .map(word => word[0].toUpperCase() + word.slice(1))
          .join(" ");
      const categoryClass = this.classificationPill(category);
      return `
              <div class="cardiology-tooltip">
                <div class="tooltip-date">${dateStr} | ${timeStr}</div>
                <div class="float-right">
                    <span class="${categoryClass}">${category}</span>
                </div>
                <div class=" ${sbpClass.join(' ')}">
                    SBP: ${data.meta.SBP.value}
                </div>
                <div class=" ${dbpClass.join(' ')}">
                    DBP: ${data.meta.DBP.value}
                </div>
                <div class="">${data.meta.sourceName}</div>
              </div>
            `
    },

    trendClass(val) {
      if (val > 0) { return 'text-danger'; }
      if (val < 0) { return 'text-success'; }
      return 'text-muted';
    },

    trendSign(val) {
      return val > 0 ? '+' : val < 0 ? '-' : '';
    },


    getClassForBP (status) {
      if (status === 'high') { return 'bp-value-high'; }
      if (status === 'low') { return 'bp-value-low'; }
      return 'bp-value-normal';
    },

    classificationPill(status) {
      if (status.toLowerCase().includes('high'))     { return 'bp-pill bp-pill-high'; }
      if (status.toLowerCase().includes('elevated')) { return 'bp-pill bp-pill-elevated'; }
      if (status.toLowerCase().includes('low'))      { return 'bp-pill bp-pill-low'; }
      if (status.toLowerCase().includes('normal')) { return 'bp-pill bp-pill-normal'; }
      return 'bp-pill';
    },

    async copyTableToClipboard() {
      const html = this.$refs.bpTable.outerHTML;

      const headers = ['Date', 'Time', 'SBP (mmHg)', 'DBP (mmHg)', 'Classification'];
      const plain = [
        headers,
        ...this.tableRows.map(row => [row.date, row.time, row.systolic, row.diastolic, row.category]),
      ].map(cols => cols.join('\t')).join('\n');

      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html':  new Blob([html],  { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]);

      this.$bvToast.toast('BP data copied to clipboard!', {
        variant: 'success',
        autoHideDelay: 3000,
        title: 'Success'
      });
    },
  },
};
</script>

<style scoped>
.smaller {
  font-size: 0.65rem;
}

.trend-badge {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.1rem;
}

.legend-circle {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-triangle {
  display: inline-block;
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: 9px solid currentColor;
  flex-shrink: 0;
}

.legend-range-box {
  display: inline-block;
  background: rgba(40, 167, 69, 0.2);
  border: 1px solid #28a745;
  font-size: 0.875em;
  font-weight: 500;
  color: #28a745;
  padding: 0.125rem 0.25rem;
}

/* Table */
.bp-table {
  font-size: 0.85rem;
}

.bp-th {
  font-size: 0.7rem;
  font-weight: 600;
  color: #6c757d;
  letter-spacing: 0.05em;
  border-top: none;
  padding-bottom: 0.5rem;
}

.bp-datetime {
  line-height: 1.3;
}

.bp-date {
  font-weight: 500;
}

.bp-time {
  font-size: 0.78rem;
  color: #6c757d;
}

.bp-unit {
  font-size: 0.75rem;
  color: #6c757d;
}

.bp-value-high   { color: #dc3545; font-weight: 500; }
.bp-value-normal { color: #28a745; font-weight: 500; }
.bp-value-low    { color: #007bff; font-weight: 500; }

.bp-value-high-bg   { background-color: #dc3545; }
.bp-value-normal-bg { background-color: #28a745; }
.bp-value-low-bg    { background-color: #007bff; }
</style>

<style>
/* Unscoped: ApexCharts renders tooltip outside component root */
.cardiology-tooltip {
  padding: 10px 14px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 0.82rem;
  min-width: 200px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
}

.cardiology-tooltip .tooltip-date {
  font-weight: 600;
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 5px;
  margin-bottom: 6px;
}

.cardiology-tooltip .sbp-value::before {
  content: '';
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.cardiology-tooltip .sbp-value.sbpIn::before {
  background: #28a745;
}
.cardiology-tooltip .sbp-value.sbpAbove::before {
  background: #dc3545;
}
.cardiology-tooltip .sbp-value.sbpBelow::before {
  background: #007bff;
}

.cardiology-tooltip .dbp-value::before {
  content: '';
  display: inline-block;
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: 9px solid currentColor;
  flex-shrink: 0;
}
.cardiology-tooltip .dbp-value.dbpIn::before {
  color: #28a745;
}
.cardiology-tooltip .dbp-value.dbpAbove::before {
  color: #dc3545;
}
.cardiology-tooltip .dbp-value.dbpBelow::before {
  color: #007bff;
}

.bp-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.875em;
  font-weight: 500;
  color: #fff;
  background: #6c757d;
}

.bp-pill-high     { background: #fde8ea; color: #c0392b; }
.bp-pill-elevated { background: #fff3cd; color: #856404; }
.bp-pill-normal   { background: #d4edda; color: #155724; }
.bp-pill-low      { background: #cce5ff; color: #004085; }

/* overwrite focus styles so buttons don't have that awful border shadow */
.btn-outline-secondary:not(:disabled):not(.disabled):active:focus,
.btn-outline-secondary:not(:disabled):not(.disabled).active:focus,
.show > .btn-outline-secondary.dropdown-toggle:focus {
  box-shadow: 0 0 .2rem 0.2rem rgba(108, 117, 125, 0.3);
}
</style>
